import { useEffect, useRef, useState } from "react";
import { SimplifiedUserProduct } from "../../Models/Product";
import { FaChevronLeft, FaChevronRight } from "react-icons/fa";
import ProductCard from "../ProductCard/ProductCard";

type ProductBarProps = {
  products: SimplifiedUserProduct[];
  altLayout?: boolean;
};

const ProductBar = ({ products, altLayout = false }: ProductBarProps) => {

  const [startIndex, setStartIndex] = useState(0);
  const [windowWidth, setWindowWidth] = useState(() => (typeof window !== "undefined" ? window.innerWidth : 1920));
  const [containerWidth, setContainerWidth] = useState(0);

  const viewportRef = useRef<HTMLDivElement | null>(null);
  const autoAdvanceRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const directionRef = useRef<1 | -1>(1);
  const touchStartXRef = useRef<number | null>(null);
  const touchStartYRef = useRef<number | null>(null);

  const getItemsPerPage = (width: number) => {
    if (width >= 1500) {
      return altLayout ? 7 : 4;
    }
    if (width >= 1200) {
      return altLayout ? 5 : 3;
    }
    if (width >= 768) {
      return altLayout ? 4 : 2;
    }
    return 2;
  };

  const itemsPerPage = getItemsPerPage(windowWidth);
  const pageCount = Math.ceil(products.length / itemsPerPage);
  const maxStartIndex = Math.max(0, products.length - itemsPerPage);
  const clampedStartIndex = Math.min(startIndex, maxStartIndex);
  const activePage = pageCount > 0
    ? Math.min(pageCount - 1, Math.floor((clampedStartIndex + itemsPerPage - 1) / itemsPerPage))
    : 0;

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const updateWidth = () => setContainerWidth(viewportRef.current?.clientWidth ?? 0);
    updateWidth();
    window.addEventListener("resize", updateWidth);
    return () => window.removeEventListener("resize", updateWidth);
  }, [itemsPerPage]);

  useEffect(() => {
    if (products.length === 0) {
      setStartIndex(0);
      return;
    }
    setStartIndex((prev) => Math.min(prev, maxStartIndex));
  }, [products.length, itemsPerPage, maxStartIndex]);

  const clearAutoAdvance = () => {
    if (autoAdvanceRef.current) {
      clearInterval(autoAdvanceRef.current);
      autoAdvanceRef.current = null;
    }
  };

  const slideBy = (direction: 1 | -1) => {
    if (products.length <= itemsPerPage) {
      return;
    }
    setStartIndex((prev) => {
      const next = prev + direction;
      return Math.min(Math.max(next, 0), maxStartIndex);
    });
  };

  const startAutoAdvance = () => {
    clearAutoAdvance();
    if (products.length <= itemsPerPage) {
      return;
    }
    autoAdvanceRef.current = setInterval(() => {
      if (directionRef.current === 1 && startIndex >= maxStartIndex) {
        directionRef.current = -1;
      } else if (directionRef.current === -1 && startIndex <= 0) {
        directionRef.current = 1;
      }
      slideBy(directionRef.current);
    }, 5000);
  };

  useEffect(() => {
    startAutoAdvance();
    return clearAutoAdvance;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [products.length, itemsPerPage, startIndex, maxStartIndex]);

  const handleNext = () => {
    slideBy(1);
    startAutoAdvance();
  };

  const handlePrev = () => {
    slideBy(-1);
    startAutoAdvance();
  };

  const handleDotClick = (pageIndex: number) => {
    if (products.length === 0 || pageIndex === activePage) {
      return;
    }

    const targetIndex = pageIndex === pageCount - 1
      ? maxStartIndex
      : Math.min(pageIndex * itemsPerPage, maxStartIndex);
    setStartIndex(targetIndex);
    startAutoAdvance();
  };

  const gapPx = windowWidth >= 640 ? 16 : 4;
  const itemWidth = containerWidth > 0
    ? Math.floor((containerWidth - gapPx * (itemsPerPage - 1)) / itemsPerPage)
    : 0;
  const translateX = itemWidth > 0 ? -(clampedStartIndex * (itemWidth + gapPx)) : 0;
  const swipeThreshold = 40;

  const handleTouchStart = (event: React.TouchEvent<HTMLDivElement>) => {
    const touch = event.touches[0];
    touchStartXRef.current = touch.clientX;
    touchStartYRef.current = touch.clientY;
  };

  const handleTouchEnd = (event: React.TouchEvent<HTMLDivElement>) => {
    const startX = touchStartXRef.current;
    const startY = touchStartYRef.current;
    touchStartXRef.current = null;
    touchStartYRef.current = null;

    if (startX == null || startY == null) {
      return;
    }

    const touch = event.changedTouches[0];
    const deltaX = touch.clientX - startX;
    const deltaY = touch.clientY - startY;

    if (Math.abs(deltaX) < swipeThreshold || Math.abs(deltaX) < Math.abs(deltaY)) {
      return;
    }

    if (deltaX < 0) {
      handleNext();
    } else {
      handlePrev();
    }
  };

  return (
    <div className={`py-2 text-center mx-auto ${altLayout ? "max-w-[1824px]" : "max-w-6xl lg:max-w-5xl"}`}>
      <div className={`relative ${altLayout ? "px-8 sm:px-12" : "px-8 sm:px-12 lg:px-0"}`}>
        <div className="flex items-center relative">
          <div
            ref={viewportRef}
            className="w-full mx-auto overflow-hidden touch-pan-y"
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
            onTouchCancel={() => {
              touchStartXRef.current = null;
              touchStartYRef.current = null;
            }}
          >
            <div
              className={`flex items-stretch gap-1 sm:gap-4 transition-transform duration-700 ease-[cubic-bezier(0.16,1,0.3,1)]`}
              style={{ transform: `translate3d(${translateX}px, 0, 0)`, willChange: "transform" }}
            >
              {products.map((product, index) => (
                <div
                  key={`${product.id}-${index}`}
                  className="p-2 sm:p-3 flex-shrink-0 h-full"
                  style={{
                    flexBasis: itemWidth > 0 ? `${itemWidth}px` : `calc((100% - ${(itemsPerPage - 1) * gapPx}px) / ${itemsPerPage})`,
                    width: itemWidth > 0 ? `${itemWidth}px` : undefined,
                    maxWidth: itemWidth > 0 ? `${itemWidth}px` : undefined
                  }}
                >
                  <ProductCard {...product} />
                </div>
              ))}
            </div>
          </div>
        </div>

        {products.length > itemsPerPage && (
          <>
            <button
              onClick={handlePrev}
              className={`text-primary-600 text-3xl sm:text-5xl transition absolute top-1/2 -translate-y-1/2 z-10 left-0 p-1.5 sm:p-0 hover:text-primary-700${!altLayout ? " lg:-translate-x-full" : ""} ${clampedStartIndex === 0 ? "opacity-30 pointer-events-none" : "opacity-100"}`}
              aria-label={"Anterior"}
            >
              <FaChevronLeft />
            </button>
            <button
              onClick={handleNext}
              className={`text-primary-600 text-3xl sm:text-5xl transition absolute top-1/2 -translate-y-1/2 z-10 right-0 p-1.5 sm:p-0 hover:text-primary-700${!altLayout ? " lg:translate-x-full" : ""} ${clampedStartIndex >= maxStartIndex ? "opacity-30 pointer-events-none" : "opacity-100"}`}
              aria-label={"Próximo"}
            >
              <FaChevronRight />
            </button>
          </>
        )}
      </div>
      {pageCount > 1 && (
        <div className="mt-4 flex justify-center gap-2">
          {Array.from({ length: pageCount }).map((_, index) => (
            <button
              key={`product-bar-dot-${index}`}
              type="button"
              onClick={() => handleDotClick(index)}
              aria-label={`Ir para a página ${index + 1}`}
              className={`h-2 w-2 rounded-full transition-all ${
                index === activePage ? "bg-primary-600 scale-110" : "bg-gray-300 hover:bg-gray-400"
              }`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default ProductBar;
